import React, { useEffect, useMemo } from "react";
import { Stack, Box, Chip, Divider, Typography } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import CampaignIcon from "@mui/icons-material/Campaign";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import FormSwitchField from "../components/common/FormSwitchField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createAnnouncement,
  updateAnnouncement,
} from "../redux/actions/announcementActions";
import { getBranches } from "../redux/actions/branchActions";
import { getClassrooms } from "../redux/actions/classroomActions";
import { getGradeLevels } from "../redux/actions/gradeLevelActions";
import { getBranchSettings } from "../redux/actions/settingsActions";
import { announcementActions } from "../redux/slices/announcement";
import { cleanFormData } from "../helpers/formHelpers";

import { AnnouncementAudience } from "../shared/enums/AnnouncementAudience";
import { AnnouncementResponseDto } from "../shared/dtos/announcement/AnnouncementResponseDto";
import { AnnouncementRequestDto } from "../shared/dtos/announcement/AnnouncementRequestDto";

const audienceOptions = [
  { value: AnnouncementAudience.ALL_USERS, label: "All Users" },
  { value: AnnouncementAudience.ALL_STAFF, label: "All Staff" },
  { value: AnnouncementAudience.TEACHERS, label: "Teachers Only" },
  { value: AnnouncementAudience.PARENTS, label: "Parents Only" },
  { value: AnnouncementAudience.STUDENTS, label: "Students Only" },
  { value: AnnouncementAudience.SPECIFIC_CLASSES, label: "Specific Classes" },
  {
    value: AnnouncementAudience.SPECIFIC_GRADE_LEVELS,
    label: "Specific Grade Levels",
  },
];

const announcementSchema = Yup.object().shape({
  title: Yup.string()
    .max(255, "Title cannot exceed 255 characters")
    .required("Title is required"),
  content: Yup.string().required("Content is required"),
  audience: Yup.string().required("Audience is required"),
  publishDate: Yup.string().nullable(),
  expiryDate: Yup.string().required("Expiry date is required"),
  sendAsSms: Yup.boolean().required(),
  targetClassroomIds: Yup.array().when("audience", {
    is: AnnouncementAudience.SPECIFIC_CLASSES,
    then: (schema) =>
      schema
        .min(1, "Select at least one class")
        .required("Classrooms are required"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  targetGradeLevelIds: Yup.array().when("audience", {
    is: AnnouncementAudience.SPECIFIC_GRADE_LEVELS,
    then: (schema) =>
      schema
        .min(1, "Select at least one grade level")
        .required("Grade levels are required"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
});

interface CreateOrUpdateAnnouncementFormProps {
  announcement?: AnnouncementResponseDto | null;
  userInfo?: any;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateAnnouncementForm: React.FC<
  CreateOrUpdateAnnouncementFormProps
> = ({
  announcement = null,
  userInfo = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const { items: branches } = useAppSelector((state) => state.branch);
  const { items: classrooms } = useAppSelector((state) => state.classroom);
  const { items: gradeLevels } = useAppSelector((state) => state.gradeLevel);
  const { branchSettings } = useAppSelector((state) => state.settings);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";
  const canSendSms = Boolean(
    branchSettings?.communicationIntegration?.active &&
    branchSettings?.communicationIntegration?.allowsSms,
  );

  useEffect(() => {
    if (isSuperAdmin && (!branches || branches.length === 0))
      dispatch(getBranches());
    if (!classrooms || classrooms.length === 0) dispatch(getClassrooms());
    if (!gradeLevels || gradeLevels.length === 0) dispatch(getGradeLevels());
    if (!branchSettings) dispatch(getBranchSettings());
  }, [
    dispatch,
    branches,
    classrooms,
    gradeLevels,
    isSuperAdmin,
    branchSettings,
  ]);

  const branchOptions = useMemo(
    () => branches?.map((b) => ({ value: b.id, label: b.name })) || [],
    [branches],
  );
  const classroomOptions = useMemo(
    () => classrooms?.map((c) => ({ value: c.id, label: c.name })) || [],
    [classrooms],
  );
  const gradeLevelOptions = useMemo(
    () => gradeLevels?.map((g) => ({ value: g.id, label: g.name })) || [],
    [gradeLevels],
  );

  const getInitialIds = (
    field: "targetClassrooms" | "targetGradeLevels",
  ): string[] => {
    if (!announcement) return [];
    const data = announcement[field];
    return Array.isArray(data) ? data.map((item) => item.id) : [];
  };

  const initialValues: AnnouncementRequestDto = {
    branchId:
      announcement?.branchId || (isSuperAdmin ? "" : userInfo?.branchId) || "",
    title: announcement?.title || "",
    content: announcement?.content || "",
    publishDate: announcement?.publishDate
      ? announcement.publishDate.substring(0, 16)
      : "",
    expiryDate: announcement?.expiryDate
      ? announcement.expiryDate.substring(0, 16)
      : "",
    audience: announcement?.audience || AnnouncementAudience.ALL_USERS,
    targetClassroomIds: getInitialIds("targetClassrooms"),
    targetGradeLevelIds: getInitialIds("targetGradeLevels"),
    sendAsSms: false,
    authorId: announcement?.authorId || userInfo?.id || "",
  };

  const formatDateTime = (dateString?: string | null): string | null => {
    if (!dateString) return null;
    return dateString.length === 16 ? `${dateString}:00` : dateString;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(announcementActions.setErrorUpdate(null))
            : () => dispatch(announcementActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={announcementSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          const cleanedValues: AnnouncementRequestDto = {
            branchId: rawCleaned.branchId,
            title: rawCleaned.title,
            content: rawCleaned.content,
            publishDate: formatDateTime(values.publishDate),
            expiryDate: formatDateTime(values.expiryDate),
            audience: values.audience,
            sendAsSms: Boolean(values.sendAsSms),
            authorId: rawCleaned.authorId,
            targetClassroomIds:
              values.audience === AnnouncementAudience.SPECIFIC_CLASSES
                ? values.targetClassroomIds
                : null,
            targetGradeLevelIds:
              values.audience === AnnouncementAudience.SPECIFIC_GRADE_LEVELS
                ? values.targetGradeLevelIds
                : null,
          };

          if (isEditing && announcement) {
            dispatch(
              updateAnnouncement({
                id: announcement.id,
                values: cleanedValues,
              }),
            );
          } else {
            dispatch(createAnnouncement(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid, values }: FormikProps<AnnouncementRequestDto>) => (
          <Form>
            <Stack spacing={2.5}>
              {isSuperAdmin && !isEditing && (
                <FormSelectField
                  name="branchId"
                  label="Branch"
                  options={branchOptions}
                />
              )}

              <FormTextField name="title" label="Announcement Title" />
              <FormTextField
                name="content"
                label="Message Content"
                multiline
                rows={4}
              />

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormTextField
                  name="publishDate"
                  label="Publish Date & Time"
                  type="datetime-local"
                  slotProps={{ inputLabel: { shrink: true } }}
                  helperText="Leave empty for immediate release"
                />
                <FormTextField
                  name="expiryDate"
                  label="Expiry Date & Time"
                  type="datetime-local"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Box>

              <Divider>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "text.secondary",
                    px: 1,
                  }}
                >
                  TARGET AUDIENCE CONFIGURATION
                </Typography>
              </Divider>

              {/* Primary Audience Dropdown using FormSelectField */}
              <FormSelectField
                name="audience"
                label="Select Primary Audience Group"
                options={audienceOptions}
              />

              {/* Classrooms Multi-Select */}
              {values.audience === AnnouncementAudience.SPECIFIC_CLASSES && (
                <FormSelectField
                  name="targetClassroomIds"
                  label="Select Target Classrooms"
                  options={classroomOptions}
                  multiple
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as string[]).map((val) => (
                        <Chip
                          key={val}
                          label={
                            classrooms?.find((c) => c.id === val)?.name || val
                          }
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                />
              )}

              {/* Grade Levels Multi-Select */}
              {values.audience ===
                AnnouncementAudience.SPECIFIC_GRADE_LEVELS && (
                <FormSelectField
                  name="targetGradeLevelIds"
                  label="Select Target Grade Levels"
                  options={gradeLevelOptions}
                  multiple
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as string[]).map((val) => (
                        <Chip
                          key={val}
                          label={
                            gradeLevels?.find((g) => g.id === val)?.name || val
                          }
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                />
              )}

              {/* SMS Dispatch using FormSwitchField */}
              {canSendSms && (
                <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                  <FormSwitchField
                    name="sendAsSms"
                    label="Also dispatch via SMS Notification (Billing rates apply)"
                  />
                </Box>
              )}

              <SubmitButton
                title={
                  isEditing ? "Update Announcement" : "Publish Announcement"
                }
                loadingText={isEditing ? "Updating..." : "Publishing..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={isEditing ? <SaveOutlinedIcon /> : <CampaignIcon />}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateAnnouncementForm;
